import React, { useCallback, useRef, useState } from 'react';
import { Modal, Input, Typography } from 'antd';

// Promise-based confirm dialog with optional reason capture — the console standard for
// sensitive/destructive actions (docs §11 governance model): confirm + reason + (the
// caller then writes) an immutable audit entry. Usage:
//
//   const { confirm, dialog } = useConfirmWithReason();
//   ...
//   const reason = await confirm({ title, message, danger: true, reasonRequired: true });
//   if (reason === null) return;         // cancelled
//   await api.terminate(id, reason);     // proceed
//   ...
//   return (<>{dialog}{/* page */}</>);

export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  okText?: string;
  danger?: boolean;
  reasonRequired?: boolean;
  reasonLabel?: string;
  /** Optional cap on the reason's length (shows a counter). */
  reasonMaxLength?: number;
  /** A phrase the operator must type to confirm (e.g. the tenant name) — extra friction for destructive ops. */
  confirmPhrase?: string;
}

interface Pending extends ConfirmOptions {
  resolve: (reason: string | null) => void;
}

const { Text } = Typography;

export function useConfirmWithReason() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [phrase, setPhrase] = useState('');
  const resolver = useRef<((r: string | null) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    setReason('');
    setPhrase('');
    return new Promise<string | null>((resolve) => {
      resolver.current = resolve;
      setPending({ ...opts, resolve });
    });
  }, []);

  const close = (result: string | null) => {
    resolver.current?.(result);
    resolver.current = null;
    setPending(null);
  };

  const reasonOk = !pending?.reasonRequired || reason.trim().length > 0;
  const phraseOk = !pending?.confirmPhrase || phrase.trim() === pending.confirmPhrase;
  const okDisabled = !reasonOk || !phraseOk;

  const dialog = (
    <Modal
      open={!!pending}
      title={pending?.title}
      okText={pending?.okText ?? 'Confirm'}
      okButtonProps={{ danger: pending?.danger, disabled: okDisabled }}
      onOk={() => close(reason.trim())}
      onCancel={() => close(null)}
      destroyOnClose
    >
      {pending?.message && <div className="mb-3 text-sm text-muted-foreground">{pending.message}</div>}
      {pending?.confirmPhrase && (
        <div className="mb-3">
          <Text className="text-sm">
            Type <Text strong>{pending.confirmPhrase}</Text> to confirm:
          </Text>
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={pending.confirmPhrase} className="mt-1" />
        </div>
      )}
      {pending?.reasonRequired !== undefined && (
        <div>
          <Text className="text-sm">{pending?.reasonLabel ?? 'Reason'}{pending?.reasonRequired ? ' (required)' : ' (optional)'}</Text>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Recorded in the audit log"
            maxLength={pending?.reasonMaxLength}
            showCount={!!pending?.reasonMaxLength}
            className="mt-1"
          />
        </div>
      )}
    </Modal>
  );

  return { confirm, dialog };
}
